import { AuthenticateWithRedirectCallback, ClerkProvider } from '@clerk/react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter, Navigate, Route, Routes, useNavigate } from 'react-router'
import { AvisadorProveedor } from '@/components/ui/Avisador'
import { Administracion } from '@/features/admin/Administracion'
import { Entrada } from '@/features/auth/Entrada'
import { RutaProtegida } from '@/features/auth/RutaProtegida'
import { Grilla } from '@/features/grilla/Grilla'
import { clerkPublishableKey, esProduccion } from '@/lib/entorno'
import { useCampoALaVista } from '@/lib/useCampoALaVista'
import { Muestrario } from '@/app/Muestrario'
import { PruebaDrive } from '@/app/PruebaDrive'
import { SinIdentidad } from '@/app/SinIdentidad'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
})

/**
 * Rutas del §4. Todo está detrás del ingreso (§8); el muestrario de la
 * Fase 0 queda en /muestrario sólo fuera de producción.
 */
export function App() {
  useCampoALaVista()
  return (
    <QueryClientProvider client={queryClient}>
      <AvisadorProveedor>
        <BrowserRouter>
          {clerkPublishableKey ? (
            <ConIdentidad clave={clerkPublishableKey} />
          ) : (
            <SinIdentidad />
          )}
        </BrowserRouter>
      </AvisadorProveedor>
    </QueryClientProvider>
  )
}

function ConIdentidad({ clave }: { clave: string }) {
  const navigate = useNavigate()
  return (
    <ClerkProvider
      publishableKey={clave}
      // Clerk navega con el router del portal. Con su navegación propia
      // (window.location) el cierre de sesión dejaba la página en blanco en
      // Chrome de Android.
      routerPush={(to) => navigate(to)}
      routerReplace={(to) => navigate(to, { replace: true })}
      signInUrl="/ingreso"
      signUpUrl="/ingreso"
      afterSignOutUrl="/ingreso"
      signInFallbackRedirectUrl="/"
      signUpFallbackRedirectUrl="/"
    >
      <Routes>
        <Route path="/" element={<Entrada />} />
        <Route path="/ingreso" element={<Entrada />} />
        <Route
          path="/sso-callback"
          element={
            <AuthenticateWithRedirectCallback
              signInFallbackRedirectUrl="/"
              signUpFallbackRedirectUrl="/"
              signInUrl="/ingreso?estado=cancelado"
              signUpUrl="/ingreso?estado=cancelado"
            />
          }
        />

        <Route element={<RutaProtegida />}>
          <Route path="/proyecto/:proyectoId" element={<Grilla />} />
        </Route>
        <Route element={<RutaProtegida solo="rastro" />}>
          <Route path="/admin" element={<Administracion />} />
          {/* Fase 6 · prueba obligatoria del selector de Drive (§5.1 del stack). */}
          {!esProduccion && <Route path="/prueba-drive" element={<PruebaDrive />} />}
          {/* OBS-006 · el video en un iPhone de verdad. Se borra al cerrarla. */}
        </Route>

        {!esProduccion && <Route path="/muestrario" element={<Muestrario />} />}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </ClerkProvider>
  )
}
